import { Card, Stack, Title, Text, Group, Button } from '@repletoryreach/sdk-ui';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { Alert, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import type { admin } from '@repletoryreach/sdk-api';
import { useT } from '../../../../i18n/I18nProvider';

type Tenant = NonNullable<Awaited<ReturnType<typeof admin.listTenants>>['data']>[number];

const MUTED = 'var(--rr-color-text-muted)';

/**
 * Tenant lifecycle: suspend, rotate keys, delete.
 *
 * None of the three endpoints exists yet. Until they do the controls are
 * disabled rather than opening a confirm dialog that resolves to a toast
 * saying nothing happened: a confirm that reaches "Delete tenant" and then
 * does nothing teaches operators to click through confirms, which is the
 * opposite of what a confirm is for.
 */
export function SettingsTab({ tenant }: { tenant: Tenant }) {
  const t = useT();

  return (
    <Stack gap="lg">
      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('tenants.view.settings.lifecycle')}</Title>
          <Stack gap="sm">
            <Row
              label={t('common.status')}
              value={
                <StatusBadge size="xs" status={tenant.is_active ? 'healthy' : 'inactive'}>
                  {tenant.is_active ? t('status.active') : t('status.inactive')}
                </StatusBadge>
              }
            />
            <Row label={t('tenants.columns.country')} value={tenant.country_iso} />
            <Row label={t('tenants.columns.timezone')} value={tenant.timezone} />
          </Stack>
          <Group gap="sm" wrap="wrap">
            <PendingAction
              label={tenant.is_active ? t('tenants.view.settings.actions.suspend') : t('tenants.view.settings.actions.reactivate')}
              tooltip={t('tenants.view.settings.notAvailable.suspend')}
            />
            <PendingAction
              label={t('tenants.view.settings.actions.rotateKeys')}
              tooltip={t('tenants.view.settings.notAvailable.rotate')}
            />
          </Group>
          <Text fz="xs" c={MUTED}>{t('tenants.view.settings.notAvailable.explainer')}</Text>
        </Stack>
      </Card>

      <Card withBorder style={{ borderColor: 'var(--rr-color-error)' }}>
        <Stack gap="md">
          <Title order={4} c="var(--rr-color-error)">{t('tenants.view.settings.dangerZone')}</Title>
          <Alert color="error" variant="light">
            {t('tenants.view.settings.dangerWarning')}
          </Alert>
          <Group gap="sm">
            <PendingAction
              label={t('tenants.view.settings.actions.delete')}
              tooltip={t('tenants.view.settings.notAvailable.delete')}
            />
          </Group>
          <Text fz="xs" c={MUTED}>{t('tenants.view.settings.notAvailable.deleteExplainer')}</Text>
        </Stack>
      </Card>
    </Stack>
  );
}

/**
 * A control whose endpoint has not shipped. The reason is both in the tooltip
 * and in the card's body text, because a tooltip on a disabled button reaches
 * neither a keyboard nor a screen-reader user.
 */
function PendingAction({ label, tooltip }: { label: string; tooltip: string }) {
  return (
    <Tooltip label={tooltip}>
      <span style={{ display: 'inline-flex' }}>
        <Button size="xs" variant="outline" disabled>
          {label}
        </Button>
      </span>
    </Tooltip>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 8, alignItems: 'baseline' }}>
      <Text fz="xs" c={MUTED}>{label}</Text>
      <span style={{ fontSize: 14 }}>{value}</span>
    </div>
  );
}
